<template>
  <div class="analytics flex flex-col flex-1">
    <div class="fixed-section sm:sticky sm:top-[61px] z-20 pt-4 bg-bg-base">
      <!-- 搜索表單 -->
      <profile-analytics-search-form />
      
      <!-- 統計概覽 -->
      <profile-analytics-summary />
      
      <!-- Loading 狀態 -->
      <div v-if="loading" class="flex justify-center items-center py-8">
        <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
      </div>
      
      <!-- 錯誤狀態 -->
      <div v-else-if="error" class="flex justify-center items-center py-8">
        <div class="text-red-500">
          Error loading data: {{ error }}
          <button @click="initializeAnalytics" class="ml-2 px-3 py-1 btn btn-primary text-white rounded">
            Retry
          </button>
        </div>
      </div>

      <!-- Analytics Nav 選單 -->
      <div v-else class="w-full bg-gray-50 pt-2">
        <div class="border-b border-gray-500">
          <button 
            v-for="tab in analyticsTabs" 
            :key="tab.key"
            :class="getTabClass(tab.key)"
            class="tab inline-block"
            @click="setActiveTab(tab.key)"
          >
            <div class="flex items-center py-3.5 hover:text-gray-200">
              {{ $t(tab.title) }}
            </div>
          </button>
        </div>
      </div>

      <!-- 模型選擇器 -->
      <div 
        v-if="showModelSelector && !loading && !error && activeTab === 'chart'"
        class="flex flex-col sm:flex-row items-start mt-4"
      >      
        <!-- 自定義可搜索下拉選擇器 -->
        <div class="relative analytics-post">
          <div class="search-input">
            <div class="search-icon" @click="handleFocus"></div>
            <input
              ref="modelInput"
              v-model="searchQuery"
              type="search"
              name="model-search"
              class="review-search-bar"
              placeholder="Search or select a model..."
              @input="handleInputChange"
              @focus="handleFocus"
              @blur="handleBlur"
              @keydown="handleKeydown"
              @search.prevent="handleInputChange"
            />
          </div>
          
          <!-- 下拉選項列表 -->
          <div 
            v-if="showDropdown"
            class="absolute top-full left-0 right-0 bg-grey-2 border border-grey-1 rounded-8px mt-1 max-h-60 overflow-y-auto z-50 shadow-lg"
            style="scrollbar-width: none; -ms-overflow-style: none;"
            ref="dropdownContainer"
          >
            <!-- All models 選項 -->
            <div
              class="text-14px px-4 py-2 hover:bg-grey-3 cursor-pointer text-white transition-colors"
              :class="{ 'bg-orange-20': highlightedIndex === -1 }"
              @mousedown.prevent="selectOption('all', 'All Models')"
              @mouseenter="highlightedIndex = -1"
            >
              All Models
            </div>
            
            <!-- Posts 選項 -->
            <div
              v-for="(post, index) in displayPosts"
              :key="post.id"
              class="text-14px px-4 py-2 hover:bg-grey-3 cursor-pointer text-white transition-colors"
              :class="{ 'bg-orange-20': highlightedIndex === index }"
              @mousedown.prevent="selectOption(post.id, post.title)"
              @mouseenter="highlightedIndex = index"
            >
              {{ post.title }}
            </div>
            
            <!-- 無結果顯示 -->
            <div 
              v-if="searchQuery && displayPosts.length === 0"
              class="px-4 py-2 text-grey-1 text-center"
            >
              No matching models found
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 圖表組件 -->
    <profile-analytics-chart v-if="activeTab === 'chart' && !loading && !error" />
    
    <!-- 表格組件 -->
    <profile-analytics-table v-if="activeTab === 'table' && !loading && !error" />
  </div>
</template>

<script>
import { ref, provide, onMounted, onUnmounted, nextTick, watch, computed, readonly } from 'vue'
import { useAnalytics } from '../utils/useAnalytics'

// Debounce 工具函數
function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export default {
  name: 'ProfileAnalytics',
  props: {
    showModelSelector: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    // 使用 analytics composable
    const analytics = useAnalytics()
    
    // 提供給子組件使用
    provide('analytics', analytics)
    
    // 解構統一的狀態和方法，使用 readonly 優化性能
    const {
      posts: postsRef,
      loading,
      error,
      initializeAnalytics,
      updateSelectedModels,
      fetchAnalyticsData
    } = analytics
    
    // 使用 readonly 包裝不需要修改的響應式數據
    const posts = readonly(postsRef)

    // 本地狀態（只保留 UI 相關的狀態）
    const selectedPostId = ref('all')
    const modelInput = ref(null)
    const dropdownContainer = ref(null)
    
    // Autocomplete 相關狀態
    const searchQuery = ref('')
    const showDropdown = ref(false)
    const highlightedIndex = ref(-1)
    
    // Analytics Tab 狀態
    const activeTab = ref('chart')
    const analyticsTabs = [
      {
        key: 'chart',
        title: 'private_profiles.analytic.models_summary'
      },
      {
        key: 'table',
        title: 'private_profiles.analytic.individual_model'
      }
    ]

    // 過濾的 posts 列表 - 優化性能
    const filteredPosts = computed(() => {
      const postsArray = posts.value
      if (!postsArray?.length || !searchQuery.value) {
        return postsArray || []
      }
      
      const query = searchQuery.value.toLowerCase()
      return postsArray.filter(post => 
        post.title?.toLowerCase().includes(query)
      )
    })

    // 顯示的 posts 列表 - 優化性能
    const displayPosts = computed(() => {
      const postsArray = posts.value
      if (!postsArray?.length || !searchQuery.value || searchQuery.value === 'All Models') {
        return postsArray || []
      }
      
      const query = searchQuery.value.toLowerCase()
      return postsArray.filter(post => 
        post.title?.toLowerCase().includes(query)
      )
    })

    // 處理輸入變化 - 加入 debouncing
    const handleInputChange = debounce(() => {
      showDropdown.value = true
      highlightedIndex.value = -1
    }, 150)

    // 處理獲取焦點
    const handleFocus = () => {
      showDropdown.value = true
      highlightedIndex.value = -1
    }

    // 處理失去焦點
    const handleBlur = () => {
      // 延遲隱藏下拉選單，讓點擊事件可以觸發
      setTimeout(() => {
        showDropdown.value = false
      }, 200)
    }

    // 處理鍵盤事件
    const handleKeydown = (event) => {
      if (!showDropdown.value) return

      const maxIndex = displayPosts.value.length - 1

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          highlightedIndex.value = highlightedIndex.value < maxIndex ? highlightedIndex.value + 1 : -1
          break
        case 'ArrowUp':
          event.preventDefault()
          highlightedIndex.value = highlightedIndex.value > -1 ? highlightedIndex.value - 1 : maxIndex
          break
        case 'Enter':
          event.preventDefault()
          if (highlightedIndex.value === -1) {
            selectOption('all', 'All Models')
          } else if (displayPosts.value[highlightedIndex.value]) {
            const post = displayPosts.value[highlightedIndex.value]
            selectOption(post.id, post.title)
          }
          break
        case 'Escape':
          showDropdown.value = false
          modelInput.value?.blur()
          break
      }
    }

    // 選擇選項
    const selectOption = (postId, title) => {
      selectedPostId.value = postId
      searchQuery.value = title
      showDropdown.value = false
      handlePostChange()
    }

    // 處理模型選擇變更 - 加入 debouncing
    const handlePostChange = debounce(() => {
      let postIds = []
      
      if (selectedPostId.value === 'all') {
        postIds = ['all']  // 明確傳遞 'all' 參數
      } else {
        postIds = [selectedPostId.value]
      }
      
      // 更新選擇的模型（只影響圖表）
      updateSelectedModels(postIds)
      
      // 重新獲取數據並更新圖表
      fetchAnalyticsData()
    }, 300)

    // Analytics Tab 切換方法
    const setActiveTab = (key) => {
      activeTab.value = key
    }

    // 獲取 Tab 的 CSS 類別
    const getTabClass = (key) => {
      return activeTab.value === key 
        ? 'border-b-4 border-primary font-medium flex-1 text-center px-4'
        : 'border-b-4 border-transparent flex-1 text-center px-4'
    }

    // 🟢 簡化：監聽 posts 變化（只用於 UI 更新）
    watch(
      () => posts.value,
      async (newPosts) => {
      },
      { immediate: false }
    )

    // 監聽 showDropdown 變化，隱藏滾動條
    watch(
      () => showDropdown.value,
      async (isVisible) => {
        if (isVisible) {
          await nextTick()
          if (dropdownContainer.value) {
            // 添加隱藏滾動條的樣式
            const style = document.createElement('style')
            style.textContent = `
              .dropdown-no-scrollbar::-webkit-scrollbar {
                display: none;
              }
            `
            if (!document.head.querySelector('[data-dropdown-style]')) {
              style.setAttribute('data-dropdown-style', 'true')
              document.head.appendChild(style)
            }
            dropdownContainer.value.classList.add('dropdown-no-scrollbar')
          }
        }
      },
      { immediate: false }
    )

    onMounted(async () => {      
      await initializeAnalytics()
      
      // 設置默認顯示文字
      searchQuery.value = 'All Models'
    })

    return {
      // 本地狀態
      selectedPostId,
      modelInput,
      dropdownContainer,
      
      // 使用統一的狀態
      posts,
      loading,
      error,
      
      // Autocomplete 狀態
      searchQuery,
      showDropdown,
      highlightedIndex,
      filteredPosts,
      displayPosts,
      
      // Analytics Tab 狀態
      activeTab,
      analyticsTabs,
      
      // Props
      showModelSelector: props.showModelSelector,
      
      // 事件處理
      handlePostChange,
      initializeAnalytics,
      handleInputChange,
      handleFocus,
      handleBlur,
      handleKeydown,
      selectOption,
      setActiveTab,
      getTabClass
    }
  }
}
</script>
